import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  User,
  Shield,
  Users,
  Bell,
  CreditCard,
  Building2,
  Settings,
  Plug,
  Palette,
  ArrowLeft,
  Menu,
  X
} from 'lucide-react';
import {
  ProfileSettings,
  SecuritySettings,
  BillingSettings,
  NotificationSettings,
  WorkspaceSettings
} from '@/components/Settings';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface AccountSettingsProps {
  isGlobalDarkMode?: boolean;
  isMobile?: boolean;
}

interface Workspace {
  id: string;
  name: string;
  color?: string;
  logo?: string;
  thumbnail?: string;
}

export default function AccountSettings({ isGlobalDarkMode = true, isMobile = false }: AccountSettingsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [workspaceTab, setWorkspaceTab] = useState<'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations'>('general');
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'manager' | 'member' | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const navigationItems = [
    { id: 'profile', label: 'My Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ];

  const workspaceTabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'roles-permissions', label: 'Roles & Permissions', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Plug }
  ];

  const currentNavItems = selectedWorkspace
    ? workspaceTabs.filter((item) => {
        if (userRole === 'member') return item.id === 'members';
        return true;
      })
    : navigationItems;

  const currentActiveId = selectedWorkspace ? workspaceTab : (activeTab === 'workspace' ? 'profile' : activeTab);

  const headerTitle = selectedWorkspace ? 'Workspace Settings' : 'Account Settings';

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user || !selectedWorkspace) {
        setUserRole(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('workspace_members')
          .select('role')
          .eq('workspace_id', selectedWorkspace.id)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setUserRole(data?.role || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
      }
    };

    fetchUserRole();
  }, [user, selectedWorkspace]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const workspaceTabFromUrl = searchParams.get('workspaceTab');
    const validTabIds = ['workspace', 'profile', 'security', 'notifications', 'billing'];
    const validWorkspaceTabIds = ['general', 'appearance', 'members', 'roles-permissions', 'integrations'];

    if (tabFromUrl && validTabIds.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
      if (tabFromUrl !== 'workspace' && selectedWorkspace) {
        setSelectedWorkspace(null);
      }
    }

    if (workspaceTabFromUrl && validWorkspaceTabIds.includes(workspaceTabFromUrl)) {
      setWorkspaceTab(workspaceTabFromUrl as 'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations');
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedWorkspace && userRole === 'member' && workspaceTab !== 'members') {
      setWorkspaceTab('members');
    }
  }, [selectedWorkspace, userRole]);

  const handleNavItemClick = (itemId: string) => {
    if (selectedWorkspace) {
      setWorkspaceTab(itemId as 'general' | 'appearance' | 'members' | 'roles-permissions' | 'integrations');
    } else {
      setActiveTab(itemId);
      const next = new URLSearchParams(searchParams);
      next.set('tab', itemId);
      next.delete('workspaceTab');
      setSearchParams(next);
    }
    setMobileDrawerOpen(false);
  };

  const handleBack = () => {
    setSelectedWorkspace(null);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'workspace');
    next.delete('workspaceTab');
    setSearchParams(next);
    setMobileDrawerOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (searchParams.has('modal')) {
        searchParams.delete('modal');
        searchParams.delete('tab');
        searchParams.delete('workspaceTab');
        setSearchParams(searchParams);
      } else {
        navigate('/home');
      }
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings isGlobalDarkMode={isGlobalDarkMode} />;
      case 'security':
        return <SecuritySettings isGlobalDarkMode={isGlobalDarkMode} />;
      case 'billing':
        return <BillingSettings isGlobalDarkMode={isGlobalDarkMode} />;
      case 'notifications':
        return <NotificationSettings isGlobalDarkMode={isGlobalDarkMode} />;
      case 'workspace':
        if (!selectedWorkspace) {
          return <ProfileSettings isGlobalDarkMode={isGlobalDarkMode} />;
        }
        return (
          <WorkspaceSettings
            isGlobalDarkMode={isGlobalDarkMode}
            onWorkspaceSelect={setSelectedWorkspace}
            selectedWorkspace={selectedWorkspace}
            workspaceTab={workspaceTab}
            onWorkspaceTabChange={setWorkspaceTab}
          />
        );
      default:
        return <ProfileSettings isGlobalDarkMode={isGlobalDarkMode} />;
    }
  };

  const borderColor = 'border-sidebar-border';
  const textColor = 'text-foreground';

  const NavList = () => (
    <ul className="space-y-1">
      {currentNavItems.map((item) => {
        const Icon = item.icon;
        const isActive = currentActiveId === item.id;
        return (
          <li key={item.id}>
            <button
              onClick={() => handleNavItemClick(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                isActive
                  ? isGlobalDarkMode
                    ? 'bg-nav-active text-white shadow-sm border-transparent'
                    : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                  : isGlobalDarkMode
                    ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  const { workspaces, refreshWorkspaces, setCurrentWorkspace: setContextWorkspace } = useWorkspace();

  const handleCreateNewWorkspace = async () => {
    if (!user) return;
    const canCreate = workspaces.length === 0 || workspaces.some(w => w.userRole === 'owner');
    if (!canCreate) {
      alert('Only workspace owners can create additional workspaces.');
      return;
    }
    
    try {
      const workspaceId = `workspace-${crypto.randomUUID()}`;
      const newWorkspace = {
        id: workspaceId,
        name: 'New Workspace',
        color: getComputedStyle(document.documentElement).getPropertyValue('--workspace-accent').trim() || '#3B82F6',
        created_by: user.id
      };

      const { error: workspaceError } = await supabase.from('workspaces').insert(newWorkspace);
      if (workspaceError) throw workspaceError;

      const { error: memberError } = await supabase.from('workspace_members').insert({
        workspace_id: workspaceId,
        user_id: user.id,
        role: 'owner',
        status: 'active'
      });
      if (memberError) throw memberError;

      await refreshWorkspaces();
      await setContextWorkspace({ ...newWorkspace, isCurrent: true } as any);
      setSelectedWorkspace({ ...newWorkspace, isCurrent: true });
      setWorkspaceTab('general');
      setActiveTab('workspace');
    } catch (err) {
      console.error(err);
      alert('Failed to create workspace.');
    }
  };

  const WorkspacesList = () => {
    if (selectedWorkspace) return null; // Only show in main account settings sidebar

    return (
      <div className="mt-6">
        <div className={`border-t mb-2 ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`} />

        <div className="flex items-center justify-between px-3 mb-2">
          <h3 className={`text-xs font-semibold uppercase tracking-wider ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
            Workspaces
          </h3>
          <button
            onClick={handleCreateNewWorkspace}
            className={`p-1 rounded transition-colors duration-200 ${isGlobalDarkMode ? 'hover:bg-sidebar-accent text-sidebar-foreground/60 hover:text-sidebar-foreground' : 'hover:bg-sidebar-light-hover text-gray-500 hover:text-gray-700'}`}
            title="Create new workspace"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
          </button>
        </div>

        <div className={`border-t mb-2 ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`} />

        <ul className="space-y-1">
          {workspaces.map((workspace) => (
            <li key={workspace.id}>
              <button
                onClick={() => {
                  setSelectedWorkspace(workspace as any);
                  setWorkspaceTab('general');
                  setActiveTab('workspace');
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-left border ${
                  isGlobalDarkMode
                    ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
                }`}
              >
                <Building2 className={`w-4 h-4 flex-shrink-0 ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`} />
                <span className="truncate">{workspace.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={handleOpenChange}>
        <DialogContent className={`max-w-md w-full h-[100dvh] p-0 flex flex-col sm:rounded-none ${isGlobalDarkMode ? 'bg-sidebar border-sidebar-border' : 'bg-white'}`}>
          <VisuallyHidden>
            <DialogTitle>{headerTitle}</DialogTitle>
          </VisuallyHidden>
          <Helmet>
            <title>Account Settings | AgencyPlus</title>
            <link rel="canonical" href="/account-settings" />
          </Helmet>

          {/* Mobile Header */}
          <div
            className={`flex-shrink-0 flex items-center justify-between gap-3 px-4 py-4 border-b ${borderColor} ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light'}`}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className={`p-2 rounded-lg transition-colors ${
                  isGlobalDarkMode
                    ? 'text-sidebar-foreground/80 hover:bg-sidebar-accent'
                    : 'text-gray-600 hover:bg-sidebar-light-hover'
                }`}
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className={`text-lg font-semibold ${textColor}`}>{headerTitle}</h2>
            </div>
            <DialogClose className={`p-2 rounded-lg transition-colors ${
              isGlobalDarkMode
                ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
                : 'hover:bg-sidebar-light-hover text-gray-600'
            }`}>
              <X className="w-5 h-5" />
            </DialogClose>
          </div>

          {/* Mobile Content */}
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              {renderContent()}
            </div>
          </div>

          {/* Mobile Drawer Overlay */}
          {mobileDrawerOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
                onClick={() => setMobileDrawerOpen(false)}
              />
              <aside
                className={`fixed left-0 top-0 w-72 max-w-[85vw] h-[100dvh] border-r flex flex-col z-[70] ${borderColor} ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light'}`}
              >
                {/* Drawer Header */}
                <div className={`px-5 py-5 border-b ${borderColor} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    {selectedWorkspace && (
                      <button
                        onClick={handleBack}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isGlobalDarkMode
                            ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-sidebar-light-hover'
                        }`}
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>
                    )}
                    <h3 className={`text-base font-semibold ${textColor}`}>{headerTitle}</h3>
                  </div>
                  <button
                    onClick={() => setMobileDrawerOpen(false)}
                    className={`p-2 rounded-lg transition-colors ${
                      isGlobalDarkMode
                        ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
                        : 'hover:bg-sidebar-light-hover text-gray-600'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Drawer Nav */}
                <nav className="flex-1 p-3 overflow-y-auto">
                  <NavList />
                  <WorkspacesList />
                </nav>
              </aside>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-w-4xl w-full h-[85vh] p-0 flex flex-row overflow-hidden border ${isGlobalDarkMode ? 'bg-background border-sidebar-border' : 'bg-white border-gray-200'}`}>
        <VisuallyHidden>
          <DialogTitle>{headerTitle}</DialogTitle>
        </VisuallyHidden>
        <Helmet>
          <title>Account Settings | AgencyPlus</title>
          <link rel="canonical" href="/account-settings" />
        </Helmet>

        <DialogClose className={`absolute right-4 top-4 z-50 p-2 rounded-lg transition-colors ${
          isGlobalDarkMode
            ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
            : 'hover:bg-sidebar-light-hover text-gray-600'
        }`}>
          <X className="w-5 h-5" />
        </DialogClose>

        {/* Left Sidebar */}
        <div
          className={`w-64 border-r flex flex-col flex-shrink-0 ${borderColor} ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light-secondary'}`}
        >
          {/* Header */}
          <div className={`px-6 py-6 border-b ${borderColor}`}>
            {selectedWorkspace ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    isGlobalDarkMode
                      ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-sidebar-light-hover'
                  }`}
                  title="Back to Workspaces"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h2 className={`text-lg font-semibold ${textColor}`}>
                  Workspace Settings
                </h2>
              </div>
            ) : (
              <h2 className={`text-lg font-semibold ${textColor}`}>
                Account Settings
              </h2>
            )}
          </div>

          {/* Navigation */}
          <nav className="p-4 flex-1 overflow-y-auto">
            <NavList />
            <WorkspacesList />
          </nav>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 overflow-y-auto h-full relative ${isGlobalDarkMode ? 'bg-background' : 'bg-white'}`}>
          <div className="flex justify-center p-8 pb-16">
            <div className="w-full max-w-2xl">
              {renderContent()}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
